/**
 * Types for Asset Group creation in Performance Max campaigns.
 *
 * Asset groups are the Performance Max equivalent of ad groups,
 * containing text assets (headlines, descriptions) and image assets.
 *
 * Requirements per Google Ads API:
 * - Headlines: 3-15 required, max 30 chars each
 * - Long Headline: 1 required, max 90 chars
 * - Descriptions: 2-5 required, max 90 chars each
 * - Business Name: 1 required, max 25 chars
 * - Landscape Images: 1+ required (1.91:1 aspect ratio)
 * - Square Images: 1+ required (1:1 aspect ratio)
 * - Logo Images: 1+ required (1:1 aspect ratio)
 */

/**
 * Form data for the Asset Group creation wizard.
 * Uses camelCase for frontend consistency.
 */
export interface AssetGroupFormData {
  name: string;
  status: 'ENABLED' | 'PAUSED';
  finalUrl: string;
  // Text assets
  headlines: string[];      // 3-15 required, 30 chars max
  longHeadline: string;     // 1 required, 90 chars max
  descriptions: string[];   // 2-5 required, 90 chars max
  businessName: string;     // 1 required, 25 chars max
  // Image assets — PendingTargeted (in-memory crops). Materialized to
  // Targeted MediaImages by the wizard's submit handler so abandoned
  // wizard runs leave zero GCS orphans. Same pattern as RDA / Demand Gen.
  landscapePending: import('./media').PendingTargeted[];  // 1+ required (1.91:1)
  squarePending: import('./media').PendingTargeted[];     // 1+ required (1:1)
  logoPending: import('./media').PendingTargeted[];       // 1+ required (1:1)
}

/**
 * API request payload for creating an asset group.
 * Uses snake_case for backend API consistency.
 */
export interface CreateAssetGroupRequest {
  name: string;
  status: 'ENABLED' | 'PAUSED';
  final_url: string;
  headlines: string[];
  long_headline: string;
  descriptions: string[];
  business_name: string;
  // Image IDs from Media Library (backend will upload to Google Ads)
  landscape_image_ids: string[];
  square_image_ids: string[];
  logo_image_ids: string[];
}

/**
 * API response after creating an asset group.
 */
export interface CreateAssetGroupResponse {
  success: boolean;
  asset_group_id: string;
  asset_group_name: string;
  campaign_id: string;
  message: string;
}

/**
 * Validation limits for asset group fields.
 */
export const ASSET_GROUP_LIMITS = {
  name: { min: 1, max: 255 },
  headline: { min: 3, max: 15, charLimit: 30 },
  longHeadline: { min: 1, max: 1, charLimit: 90 },
  description: { min: 2, max: 5, charLimit: 90 },
  businessName: { min: 1, max: 1, charLimit: 25 },
  landscapeImage: { min: 1, max: 20 },
  squareImage: { min: 1, max: 20 },
  logoImage: { min: 1, max: 5 },
} as const;

/**
 * Default form data for a new asset group.
 */
export const DEFAULT_ASSET_GROUP_FORM_DATA: AssetGroupFormData = {
  name: '',
  status: 'ENABLED',
  finalUrl: '',
  headlines: ['', '', ''], // Start with minimum 3 empty headlines
  longHeadline: '',
  descriptions: ['', ''], // Start with minimum 2 empty descriptions
  businessName: '',
  landscapePending: [],
  squarePending: [],
  logoPending: [],
};
